import { WifiOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Card, Flex, Skeleton, Table, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { currentIntlLocale } from '@/app/i18n'
import { fetchUsage, summarizeUsage, type DayUsage } from '@/features/usage/usageApi'
import { UsageChart } from '@/features/usage/UsageChart'
import { UsageSummaryCard } from '@/features/usage/UsageSummaryCard'
import { queryKeys } from '@/shared/api/queryKeys'
import { formatBytes, getDisplayTimeZone, zoneOffsetLabel } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { LoadError } from '@/shared/ui/LoadError'

/**
 * `day` — `YYYY-MM-DD`, backendda TENANT zonasining kun chegarasi bilan
 * hisoblangan (`UsageController`). Ya'ni bu allaqachon "tayyor" kun yorlig'i,
 * uni QAYTA zonaga o'tkazish MUMKIN EMAS: `Date.parse` uni brauzerning
 * mahalliy zonasida talqin qilardi va manfiy ofsetli zonalarda bir kun
 * orqaga surib qo'yardi. Shuning uchun bu yerda ANIQ UTC formatlagich
 * ishlatiladi — u satrni qanday bo'lsa shundayligicha ko'rsatadi —
 * `formatDate` (ISO LAHZA uchun mo'ljallangan) emas.
 */
function formatDay(day: string, locale: string): string {
  const d = new Date(`${day}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return day
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(d)
}

export function UsagePage() {
  const { t } = useTranslation()
  const usageQuery = useQuery({ queryKey: queryKeys.usage, queryFn: () => fetchUsage() })
  const locale = currentIntlLocale()

  const summary = summarizeUsage(usageQuery.data ?? [])

  return (
    <Flex vertical gap={16}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('usage.title')}
        </Typography.Title>
        <Typography.Text type="secondary">{t('usage.subtitle')}</Typography.Text>
        {/*
          Kun chegarasi operator zonasida — abonent buni BILISHI kerak,
          aks holda "bugungi trafik" nega uning yarim tuniga to'g'ri
          kelmayotgani tushunarsiz bo'lib qoladi.
        */}
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
          {t('timezone.dayHint', { zone: getDisplayTimeZone(), offset: zoneOffsetLabel() })}
        </Typography.Text>
      </div>

      {usageQuery.isPending ? (
        <Card><Skeleton active paragraph={{ rows: 6 }} /></Card>
      ) : usageQuery.isError ? (
        <LoadError
          titleKey="usage.loadFailed"
          error={usageQuery.error}
          onRetry={() => void usageQuery.refetch()}
        />
      ) : usageQuery.data.length === 0 ? (
        <EmptyState icon={<WifiOutlined />} title={t('usage.empty')} />
      ) : (
        <>
          <UsageSummaryCard summary={summary} />

          <Card>
            <UsageChart data={usageQuery.data} />
          </Card>

          <Card styles={{ body: { padding: 0 } }}>
            <Table<DayUsage>
              rowKey="day"
              dataSource={[...usageQuery.data].reverse()}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              scroll={{ x: true }}
              columns={[
                {
                  title: t('usage.columns.date'),
                  dataIndex: 'day',
                  render: (v: string) => formatDay(v, locale),
                },
                {
                  title: t('usage.columns.download'),
                  dataIndex: 'downloadBytes',
                  render: (v: number) => formatBytes(v),
                },
                {
                  title: t('usage.columns.upload'),
                  dataIndex: 'uploadBytes',
                  render: (v: number) => formatBytes(v),
                },
                {
                  title: t('usage.columns.total'),
                  dataIndex: 'totalBytes',
                  render: (v: number) => formatBytes(v),
                },
                ...(summary.freeBytes > 0
                  ? [
                      {
                        title: t('usage.columns.free'),
                        dataIndex: 'freeBytes',
                        render: (v: number) => formatBytes(v),
                      },
                    ]
                  : []),
              ]}
            />
          </Card>
        </>
      )}
    </Flex>
  )
}
